import { Container, ListItemRenderer } from '@acies/core';
import { Dropdown, DropdownEvent, DropdownItem, Icon, InputField, InputFieldEvent, Label } from '@acies/ui';
import { type ExpressionParameter, GeometryParameter, type IPresentedParameter, type IStockConfigurationParameter, isStockParameter, LookupParameter, StockAnswer, StockNumberParameter, StockStringParameter, type StringParameter } from '../../../domain/configurations';
import type { SearchResult } from '../../../domain/search-results';
import { type ISideType, SideTypes } from '../../../domain/side-types';
// aliased, because the component property it is assigned to is also called `search`
import { searchEffect as searchCollection } from '../../../services/search';
import SearchAutoComplete from '../shared/search-auto-complete/search-auto-complete';
import SearchAutoCompleteEvent from '../shared/search-auto-complete/search-auto-complete-event';
import AnswerItemRendererEvent from './answer-item-renderer-event';

/**
 * The item the side dropdown offers each side as, keyed by the side.
 *
 * Built once and shared by every row: a DropdownItem is never changed, and a row picks among these very instances, since the dropdown finds its pick by identity. Keyed rather than searched, so every side the schema can decode has its item by construction and there is no side to not find.
 *
 * Asserted to the full Record because `Object.fromEntries` only knows it was handed strings; it was handed every one of {@link SideTypes}.
 */
const sideItemFor = Object.fromEntries(SideTypes.literals.map((side) => [side, new DropdownItem({ text: side, value: side })])) as Record<ISideType, DropdownItem>;

/** Every side, as the side dropdown offers them, in the order {@link SideTypes} keeps them. */
const sideItems = SideTypes.literals.map((side) => sideItemFor[side]);

export default class AnswerItemRenderer extends ListItemRenderer<IPresentedParameter> {
	/**
	 * The parameter the row showed last, kept so the next one can be told to be the same question asked again — see itemChanged.
	 *
	 * Kept apart from `item`, which is already the next parameter by the time itemChanged runs. Null is a row that showed nothing, or was emptied on its way into the pool.
	 */
	#shown: IPresentedParameter | null;

	/**
	 * Whether the user has typed in the row's own field since it was focused.
	 *
	 * What tells a field the user is answering in from one they are merely standing in — the two are left alone differently when the parameter is handed to the row again; see #seedInputField and #inputFieldBlurred. The picker keeps the same for its own field.
	 */
	#typed: boolean;

	/**
	 * The code of the answer the row last sent for a stock parameter answered from a list, or null while it has sent none since it was handed the parameter.
	 *
	 * What a pick is measured against while it is set, rather than the answer the parameter holds: the parameter is not handed back until the API has answered, so picking the answer it still holds after picking another is the user going back on the one that is out — a change, and the one that has to win. Measured
	 * against the parameter, it would be taken for no change at all, and the answer still out would land over the user's last pick.
	 *
	 * Forgotten when the row is handed a parameter, the same one made anew included: that is the API's answer, and from then on the parameter holds what was sent.
	 */
	#sentAnswer: string | null;

	/** The items the answer dropdown offers the parameter's answers as, so the one the parameter holds can be picked among them. Built afresh for each parameter, since the dropdown finds its pick by identity. */
	#answerItems: ReadonlyArray<DropdownItem>;

	#answerDropdown!: Dropdown;
	#icon!: Icon;
	#iconContainer!: Container;
	#inputField!: InputField;
	#menuIcon!: Icon;
	#nameLabel!: Label;
	#searchAutoComplete!: SearchAutoComplete<SearchResult>;
	#sideDropdown!: Dropdown;
	#statusContainer!: Container;
	#statusIcon!: Icon;
	#targetLabel!: Label;
	public constructor() {
		super();
		this.#answerItems = [];
		this.#sentAnswer = null;
		this.#shown = null;
		this.#typed = false;
		this.width = 'fill';
		this.height = 32;
		this.gapHorizontal = 16;
		this.paddingHorizontal = 16;
		this.alignment = 'left';
		// the row is somewhere to put controls, and the controls are what the user works with, so Tab
		// goes to them and not to the thing holding them. Out of the tab order rather than unfocusable:
		// a pointer and focus() can still reach the row, which is what tabbable is for
		this.tabbable = false;
	}

	public get icon(): Icon {
		if (!this.#icon) {
			this.#icon = new Icon();
			this.#icon.name = 'perspective-02';
		}
		return this.#icon;
	}

	/**
	 * The column the parameter's icon sits in, at the left hand end of the row.
	 *
	 * A Container of its own rather than the Icon placed straight in the row, so the column keeps its width whether or not there is an icon to show: what is shown differs from one parameter to the next, and a row that dropped its icon would otherwise pull every column after it out of line with the ones above.
	 */
	public get iconContainer(): Container {
		if (!this.#iconContainer) {
			this.#iconContainer = new Container();
			this.#iconContainer.width = 32;
			this.#iconContainer.height = 'fill';
			this.#iconContainer.alignment = 'center';
			this.#iconContainer.addComponent(this.icon);
		}
		return this.#iconContainer;
	}

	/**
	 * What the parameter is called, which is what the user finds the row by.
	 *
	 * The engine writes it in Danish and already spelled for a reader — `Rammefelt 1: Glasspacer` — so there is nothing to translate and no list of labels to keep in step with the engine's. Read through {@link ConfigurationParameter.label}, which takes it off the attribute, so a row cannot show a name that disagrees
	 * with the attribute it names.
	 */
	public get nameLabel(): Label {
		if (!this.#nameLabel) {
			this.#nameLabel = new Label();
			this.#nameLabel.width = 'fill';
		}
		return this.#nameLabel;
	}

	/**
	 * The picker a lookup parameter's value is chosen in, searching the collection that parameter points at.
	 *
	 * Out of the layout until a lookup parameter is handed to the row, rather than hidden in place: it shares its column with {@link AnswerItemRenderer.inputField}, and only one of the two is ever the control for a given parameter. `visible` is what takes it out — `visibility` would leave the column twice as wide as
	 * the header says it is.
	 *
	 * What it searches is set per parameter and not here, since each one is picked from a collection of its own.
	 */
	public get searchAutoComplete(): SearchAutoComplete<SearchResult> {
		if (!this.#searchAutoComplete) {
			this.#searchAutoComplete = new SearchAutoComplete<SearchResult>();
			this.#searchAutoComplete.visible = false;
			this.#searchAutoComplete.width = 208;
		}
		return this.#searchAutoComplete;
	}

	/**
	 * The field a string parameter's value is typed into.
	 *
	 * The other half of the same column — see {@link AnswerItemRenderer.searchAutoComplete} for why both are out of the layout until the row knows which kind it is showing.
	 */
	public get inputField(): InputField {
		if (!this.#inputField) {
			this.#inputField = new InputField();
			this.#inputField.visible = false;
			this.#inputField.width = 208;
		}
		return this.#inputField;
	}

	/**
	 * The dropdown a stock parameter answered from a list is answered in, offering the answers by their codes.
	 *
	 * The third control in the column {@link AnswerItemRenderer.inputField} and {@link AnswerItemRenderer.searchAutoComplete} share, and out of the layout until a parameter that is answered from a list is handed to the row, for the reason the other two are. The code is what is shown — `Hvid`, `Grå` — since the column
	 * is too narrow for the engine's longer names.
	 *
	 * A pick is answered with {@link AnswerItemRendererEvent.ANSWER_CHANGED} — see #answerPicked.
	 */
	public get answerDropdown(): Dropdown {
		if (!this.#answerDropdown) {
			this.#answerDropdown = new Dropdown();
			this.#answerDropdown.visible = false;
			this.#answerDropdown.width = 208;
			this.#answerDropdown.minWidth = 208;
		}
		return this.#answerDropdown;
	}

	/**
	 * What the parameter is about at all: the model as a whole, or one of its sashes.
	 *
	 * The engine's own shorthand, shown as the engine's — `MODL`, `RM1` — rather than spelled out, because it is a code to recognise and not a sentence to read. Upper case because that is how the engine writes these everywhere else a user meets them; the value itself arrives lower case.
	 *
	 * It is what tells two otherwise identical rows apart: the profile set of the first sash and of the second name the same component and the same attribute, and differ only here.
	 */
	public get targetLabel(): Label {
		if (!this.#targetLabel) {
			this.#targetLabel = new Label();
			this.#targetLabel.width = 64;
		}
		return this.#targetLabel;
	}

	/**
	 * Which side of the window the parameter applies to, where it applies to one at all, picked from every side there is.
	 *
	 * Only some parameters have a side — the hardware of a frame field does, its surface treatment does not, and a string parameter never has one — so this is hidden as often as it is shown. Hidden rather than taken out of the layout: the column is a fixed 72 wide and the ones after it line up against the header, so a
	 * row that dropped it would pull Status out of line with every row that kept it.
	 *
	 * Each side is offered as the engine's own code, which is already upper case: `T` is the top, `VI` the inside of the left. The code is the value as well as the text, since it is what a pick would be sent back as.
	 *
	 * A pick is answered with {@link AnswerItemRendererEvent.SIDE_CHANGED} — see #sideChanged.
	 */
	public get sideDropdown(): Dropdown {
		if (!this.#sideDropdown) {
			this.#sideDropdown = new Dropdown();
			this.#sideDropdown.width = 72;
			this.#sideDropdown.minWidth = 72;
			this.#sideDropdown.data = sideItems;
			this.#sideDropdown.visibility = false;
		}
		return this.#sideDropdown;
	}

	public get statusIcon(): Icon {
		if (!this.#statusIcon) {
			this.#statusIcon = new Icon();
			this.#statusIcon.name = 'check-circle';
		}
		return this.#statusIcon;
	}

	/**
	 * The column at the right hand end of the row, saying how the parameter came to hold what it holds.
	 *
	 * One icon for now, shown on every row. A parameter is in one of three states — the engine's default, a value somebody overrode it with, one the engine worked out from the others — and telling them apart is what this column is for; until it does, it says the same thing everywhere rather than nothing anywhere.
	 *
	 * A Container of its own for the same reason the icon at the other end has one: the column keeps its 48 whatever it ends up holding, so the rows stay lined up with the header and with each other.
	 */
	public get statusContainer(): Container {
		if (!this.#statusContainer) {
			this.#statusContainer = new Container();
			this.#statusContainer.width = 48;
			this.#statusContainer.height = 'fill';
			this.#statusContainer.alignment = 'center';
			this.#statusContainer.addComponent(this.statusIcon);
		}
		return this.#statusContainer;
	}

	public get menuIcon(): Icon {
		if (!this.#menuIcon) {
			this.#menuIcon = new Icon();
			this.#menuIcon.name = 'dots-vertical';
		}
		return this.#menuIcon;
	}

	/**
	 * Puts the side the parameter holds back in the dropdown, after the API would not take the one the user picked.
	 *
	 * The one refusal a row does anything about. A refused value is left where the user typed it, to be corrected, and what went wrong is said by the position's error card rather than marked on the control — a mark says that something was refused and not what. A side is different: the dropdown would otherwise go on
	 * showing a side the parameter does not have, so it is put back. Putting it back picks it in the dropdown, which the row hears as a pick like any other — and says nothing about, since it is the side the parameter has.
	 */
	public rejectSide(): void {
		this.#showSide();
	}

	protected override initialize(): void {
		super.initialize();
		this.addComponents([this.iconContainer, this.nameLabel, this.searchAutoComplete, this.inputField, this.answerDropdown, this.targetLabel, this.sideDropdown, this.statusContainer, this.menuIcon]);
		this.#addEventListeners();
	}

	/**
	 * Every event the row's controls raise is caught here, and none of them leave.
	 *
	 * Three of them say something the row has to answer — a lookup's value settling, a text field being left, and a side being picked — and the rest say nothing anyone above the row can use. All of them are stopped either way, because a row is made of a picker, a field and a dropdown, and nothing outside it should be
	 * able to tell.
	 *
	 * The first two are the moments a value is done being changed rather than in the middle of it. A lookup says so itself with VALUE_CHANGED, which covers a pick and a typed key alike. A text field has no such event, so the row uses BLURRED — and Enter is covered by it, because Input blurs the field after raising
	 * ENTER, so pressing Enter always produces a blur too. A side is done the moment it is picked, so the dropdown's own event is the moment.
	 */
	#addEventListeners(): void {
		const stop = this.#stopEvent as EventListener;
		this.addEventListener(DropdownEvent.SELECTED_CHANGED, this.#dropdownChanged as EventListener);
		this.addEventListener(SearchAutoCompleteEvent.VALUE_CHANGED, this.#lookupValueChanged as EventListener);
		this.addEventListener(SearchAutoCompleteEvent.BLURRED, stop);
		this.addEventListener(InputFieldEvent.BLURRED, this.#inputFieldBlurred as EventListener);
		this.addEventListener(InputFieldEvent.CHANGED, this.#inputFieldChanged as EventListener);
		this.addEventListener(InputFieldEvent.ENTER, stop);
		this.addEventListener(InputFieldEvent.FOCUSED, this.#inputFieldFocused as EventListener);
		this.addEventListener(InputFieldEvent.HOVERED, stop);
		this.addEventListener(InputFieldEvent.UN_HOVERED, stop);
	}

	/**
	 * Answers a text field the user has finished with, when what it holds is not what the parameter holds.
	 *
	 * Compared against the parameter rather than against something the row remembers, because the parameter is what the field was filled from and so is already the value a change is measured against. Leaving a field untouched, or putting it back the way it was found, changes nothing and says nothing.
	 *
	 * The target is checked because a lookup row has a field of its own inside its picker. SearchAutoComplete stops that field's BLURRED before it gets here, so today this could not be anything else — but the row should not be the thing that breaks the day it stops.
	 */
	#inputFieldBlurred(event: InputFieldEvent): void {
		event.stopImmediatePropagation();
		if (event.target !== this.inputField) {
			return;
		}
		const typed = this.#typed;
		this.#typed = false;
		// a lookup is answered through its picker, and a row holding nothing is a pooled row waiting
		// to be handed something
		// and a geometry is not answered in the row at all
		if (this.item === null || this.item instanceof LookupParameter || this.item instanceof GeometryParameter) {
			return;
		}
		const held = textOf(this.item);
		// nothing typed is nothing said, whatever the field shows: the parameter may have been given
		// another value while the user stood in the field — see #seedInputField — and that value is
		// shown now that they have left, rather than the old one being sent back as their answer
		if (!typed) {
			this.inputField.text = held;
			return;
		}
		if (this.inputField.text === held) {
			return;
		}
		this.dispatchEvent(new AnswerItemRendererEvent(AnswerItemRendererEvent.ANSWER_CHANGED, this.item, this.inputField.text));
	}

	/** Remembers that the user has typed in the row's own field, which is what makes what it holds theirs. The event says nothing outside the row. */
	#inputFieldChanged(event: InputFieldEvent): void {
		event.stopImmediatePropagation();
		if (event.target === this.inputField) {
			this.#typed = true;
		}
	}

	/** A field just focused has not been typed in yet, whatever happened the last time it was. The event says nothing outside the row. */
	#inputFieldFocused(event: InputFieldEvent): void {
		event.stopImmediatePropagation();
		if (event.target === this.inputField) {
			this.#typed = false;
		}
	}

	/**
	 * Answers a lookup whose value has settled, however the user got there.
	 *
	 * No comparison here: SearchAutoComplete has already made it, against the key the row seeded it with — see its `text` setter. What reaches this has moved.
	 */
	#lookupValueChanged(event: SearchAutoCompleteEvent<SearchResult>): void {
		event.stopImmediatePropagation();
		if (this.item === null) {
			return;
		}
		this.dispatchEvent(new AnswerItemRendererEvent(AnswerItemRendererEvent.ANSWER_CHANGED, this.item, event.text, event.selectedItem));
	}

	/**
	 * Answers a side picked in the dropdown, when it is not the side the parameter holds.
	 *
	 * Compared against the parameter, because the row picks in the dropdown itself: it picks the parameter's side each time it is handed one, and again when a pick is refused, and those are picks the dropdown reports like any other. What the parameter already holds is not a change.
	 *
	 * Nothing is picked is not a side either. Clicking the side that is already picked takes it off again, but a parameter that applies to a side always applies to one, so the row puts it back rather than saying anything. The rows are pooled as well, and emptying one takes its pick off before it is handed the next
	 * parameter — which is why a row holding no sided lookup has nothing to say about any of this.
	 */
	/** A pick in either of the row's dropdowns, told apart by which dropdown raised it: the answer a stock parameter is given, or the side a lookup applies to. */
	#dropdownChanged(event: DropdownEvent): void {
		if (event.target === this.answerDropdown) {
			this.#answerPicked(event);
		} else {
			this.#sideChanged(event);
		}
	}

	/**
	 * Answers an answer picked for a stock parameter answered from a list, when it is not the answer the parameter holds.
	 *
	 * Compared against the parameter, for the reason #sideChanged compares a side: the row picks the parameter's answer itself each time it is handed one, and the dropdown reports that pick like any other. While an answer the row sent is out, against that one instead — see #sentAnswer. The answer goes as its code as
	 * the engine wrote it — `Gr%C3%A5` — since that is what the engine handed over.
	 *
	 * Nothing picked is not an answer either. Clicking the answer that is already picked takes it off again, so the row puts it back rather than saying anything, the way it puts a side back.
	 */
	#answerPicked(event: DropdownEvent): void {
		event.stopImmediatePropagation();
		const parameter = this.item;
		if (!(parameter instanceof StockStringParameter)) {
			return;
		}
		if (!(event.value instanceof StockAnswer)) {
			// once the click that took it off is over, for the reason #sideChanged waits
			queueMicrotask(() => {
				this.#showAnswer(parameter);
			});
			return;
		}
		if (event.value.key === this.#pickedAnswer(parameter)) {
			return;
		}
		this.#sentAnswer = event.value.key;
		this.dispatchEvent(new AnswerItemRendererEvent(AnswerItemRendererEvent.ANSWER_CHANGED, parameter, event.value.key));
	}

	#sideChanged(event: DropdownEvent): void {
		event.stopImmediatePropagation();
		if (!(this.item instanceof LookupParameter) || this.item.sideType === null) {
			return;
		}
		if (event.value === null) {
			// once the click that took it off is over, rather than from inside it: the dropdown picks by
			// clicking the item renderer, and click() does nothing on an element whose own click() is still
			// running — which is where this is, whenever the pick was taken off by code rather than a pointer
			queueMicrotask(() => {
				this.#showSide();
			});
			return;
		}
		if (event.value === this.item.sideType) {
			return;
		}
		// asserted rather than checked: every item the dropdown holds is one of sideItems, whose values are sides
		this.dispatchEvent(new AnswerItemRendererEvent(AnswerItemRendererEvent.SIDE_CHANGED, this.item, event.value as ISideType));
	}

	/** Stops an event the row's controls raised that says nothing outside the row. Stopped rather than ignored, so a listener above cannot hear a child it has no business knowing about. */
	#stopEvent(event: Event): void {
		event.stopImmediatePropagation();
	}

	/**
	 * Presents whichever kind of parameter the row has been handed, or nothing at all.
	 *
	 * Four routes, because a row is one of four things and each shows a different set of its children: a value picked from a collection, text typed into a field, a shape that is shown but not changed here, or an empty renderer waiting to be handed one. The renderers are pooled and reused, so the null route is not a
	 * corner case — it is the state a row is put back into before it is handed the next parameter, and anything the previous one left on screen is still there until it is cleared.
	 *
	 * The kinds are told apart by the classes themselves rather than by a field, which is what {@link ConfigurationParameters} being a union of classes buys: `instanceof` narrows, so each route is handed the kind it presents and can read what only that kind has — the collection a lookup is picked from, the string a
	 * text parameter holds, the shape a geometry is cut to.
	 */
	protected override itemChanged(): void {
		super.itemChanged();
		// whatever the row sent has been answered, or belongs to a parameter the row no longer shows
		this.#sentAnswer = null;
		const shown = this.#shown;
		this.#shown = this.item;
		// the same question asked again, answered anew by the API — a changed configuration is made
		// of new parameter objects — is an update of the row where it stands, and not a new row: the
		// controls stay, and a field the user is in stays theirs. See #update
		if (this.item !== null && shown !== null && shown.identity === this.item.identity && shown.constructor === this.item.constructor) {
			this.#update(this.item);
			return;
		}
		// the row is emptied first, so each route only has to turn its own control on. A pooled row
		// is handed the next parameter directly, without being put back to null in between, so the
		// alternative is every route undoing whatever every other route might have left behind
		this.#clear();
		if (this.item === null) {
			return;
		}
		this.#show(this.item);
	}

	/** Presents a parameter on an emptied row. */
	#show(parameter: IPresentedParameter): void {
		this.#showCommon(parameter);
		if (isStockParameter(parameter)) {
			this.#showStockParameter(parameter);
		} else if (parameter instanceof LookupParameter) {
			this.#showLookupParameter(parameter);
		} else if (parameter instanceof GeometryParameter) {
			this.#showGeometryParameter(parameter);
		} else {
			// the two kinds left are both a value held as text, so both are typed into the same field
			this.#showTextParameter(parameter);
		}
		this.#showSide();
	}

	/**
	 * Presents the same parameter again, as the API has made it anew, on a row that is already showing it.
	 *
	 * Everything the row reads off the parameter is read again, since any of it may have moved: the engine couples parameters, so an answer to one can change the state, the side or the value of another the user never touched. The value is the one thing not simply written: a field the user is in, or has typed in, is
	 * theirs until they leave it — the user wins over the API — and the picker and the field each know how to take a value without taking the field. See SearchAutoComplete.seed and #seedInputField.
	 *
	 * The kinds are told apart as #show tells them apart, and the row got here only because the kind is the one it was showing, so the control already on screen is the right one.
	 */
	#update(parameter: IPresentedParameter): void {
		this.#showCommon(parameter);
		if (parameter instanceof StockStringParameter && parameter.answers !== null) {
			// the answers are the new parameter's, so the dropdown is handed them afresh; there is
			// nothing typed to leave alone in it
			this.inputField.visible = false;
			this.#showAnswerList(parameter, parameter.answers);
		} else if (isStockParameter(parameter)) {
			// whether the user may change it is the engine's to say, and it may have said otherwise this time
			this.answerDropdown.visible = false;
			this.inputField.readOnly = parameter.isReadOnly;
			this.inputField.visible = true;
			this.#seedInputField(textOf(parameter));
		} else if (parameter instanceof LookupParameter) {
			this.searchAutoComplete.search = (limit, query) => searchCollection(parameter.values.self, limit, query);
			this.searchAutoComplete.seed(parameter.key ?? '');
		} else if (parameter instanceof GeometryParameter) {
			// not something the user types, so there is nothing of theirs to leave alone
			this.inputField.text = parameter.vip;
		} else {
			this.#seedInputField(parameter.value);
		}
		this.#showSide();
	}

	/**
	 * The name, the target and the state, which every kind has in common — all three read off the parameter, which every kind carries them on.
	 *
	 * A stock item's parameters are about the item as a whole and name no target, so their target column is left empty rather than filled with something no model parameter would say.
	 */
	#showCommon(parameter: IPresentedParameter): void {
		this.nameLabel.text = parameter.label;
		this.targetLabel.text = isStockParameter(parameter) ? '' : parameter.targetType.toUpperCase();
		// a value somebody overrode is pinned: it holds whatever the engine would otherwise have made of it
		this.icon.name = parameter.stateType === 'override' ? 'pin-01' : 'perspective-02';
	}

	/**
	 * Puts the value a text parameter now holds in the row's field, unless the user is in the field or has typed in it.
	 *
	 * Left alone, what they typed stays theirs, and is measured against this value when they leave — see #inputFieldBlurred, which reads the value off the parameter by then. Typed nothing, the field shows this value when they leave. The picker does the same for a lookup; see SearchAutoComplete.seed.
	 */
	#seedInputField(value: string): void {
		if (this.#typed || this.inputField.contains(document.activeElement)) {
			return;
		}
		this.inputField.text = value;
	}

	/**
	 * Takes down whatever the previous parameter left on screen, so the row starts empty.
	 *
	 * The picker is reset rather than only emptied and hidden: it may be holding the answer to a search, a pick made in it, and a search still out — and none of those mean anything to the parameter the row is about to show. A search still out is the one that would otherwise be seen, since it answers into the row long
	 * after it has moved on.
	 */
	#clear(): void {
		// a refusal belongs to the answer that was refused, and a pooled row is about to show another
		this.#typed = false;
		this.inputField.error = false;
		this.searchAutoComplete.error = false;
		this.icon.name = 'perspective-02';
		this.inputField.readOnly = false;
		this.inputField.text = '';
		this.inputField.type = 'text';
		this.inputField.visible = false;
		this.nameLabel.text = '';
		this.searchAutoComplete.reset();
		this.searchAutoComplete.search = null;
		this.searchAutoComplete.visible = false;
		this.sideDropdown.selectedItem = null;
		this.sideDropdown.visibility = false;
		this.targetLabel.text = '';
		this.#answerItems = [];
		this.answerDropdown.data = null;
		this.answerDropdown.enabled = true;
		this.answerDropdown.visible = false;
	}

	/**
	 * Presents a parameter whose value is picked from a collection the engine keeps.
	 *
	 * The collection differs from one parameter to the next, so what the picker searches is set here rather than once when it is built. `values.self` is handed over exactly as it arrived — the engine writes these links with a prefix and a store spelling of its own, so rebuilding one from its parts produces a path that
	 * does not resolve.
	 *
	 * The field is seeded with the code the value is stored under, which is all there is to show until the collection has been fetched: the name beside it lives on the value in that collection. A lookup nobody has answered yet has no code, and seeds the field with nothing — which is what an open question looks like,
	 * and what makes the first key typed into it a change. Setting the text dispatches nothing, so seeding a row does not put a search out.
	 */
	#showLookupParameter(parameter: LookupParameter): void {
		this.searchAutoComplete.search = (limit, query) => searchCollection(parameter.values.self, limit, query);
		// through the component rather than into its field, so the key the parameter already holds is
		// what a change is measured against: a row seeded and then tabbed through has not been changed
		this.searchAutoComplete.text = parameter.key ?? '';
		this.searchAutoComplete.visible = true;
	}

	/**
	 * Presents a parameter whose value is the shape the window is cut to, by the code the engine writes beside the shape.
	 *
	 * In the same field a text parameter is typed into, so the value sits in the column every other row has it in, but read-only: a shape is not something a row can take as text, and changing one is the geometry dialog's job, which shows the shapes as drawings to pick from. The measurements are left out for now.
	 */
	#showGeometryParameter(parameter: GeometryParameter): void {
		this.inputField.text = parameter.vip;
		this.inputField.readOnly = true;
		this.inputField.visible = true;
	}

	/**
	 * Shows the side the parameter applies to, or hides the dropdown when it applies to none.
	 *
	 * Null is a parameter that applies to no one side, which is most of them — and a side is something only a lookup can have, so a text parameter is one with none. Hidden rather than taken out of the layout, for the reason {@link AnswerItemRenderer.sideDropdown} gives.
	 *
	 * Read off the parameter every time rather than remembered, so putting a refused side back and showing a new parameter are the same thing.
	 */
	/**
	 * Shows the side the parameter applies to, and lets it be picked only once the lookup is answered.
	 *
	 * A side is where the chosen value is mounted, so on a lookup nobody has answered there is nothing to mount yet. Picked there, the lookup would stay pending — see LookupParameter.withSide — and be left out of what is sent, so the side would be lost without a word. So the side is shown, since the engine gave one,
	 * but cannot be changed until a value has been picked.
	 */
	#showSide(): void {
		const lookup = this.item instanceof LookupParameter ? this.item : null;
		const side = lookup === null ? null : lookup.sideType;
		this.sideDropdown.selectedItem = side === null ? null : sideItemFor[side];
		this.sideDropdown.visibility = side !== null;
		this.sideDropdown.enabled = lookup !== null && side !== null && lookup.key !== null;
	}

	/**
	 * Presents a parameter whose value is held as text: one the user typed, or one the engine worked out.
	 *
	 * The value itself, and not a code to be looked up. An empty one stays empty, which is what an optional parameter nobody has filled in looks like.
	 *
	 * Both kinds get the same field for now, which is a decision about the control rather than about the kinds: an expression is a value the engine derives, and a field the user can type into is not obviously the right way to show one. They stay separate classes, so the day that is settled this route splits without
	 * anything else having to change.
	 */
	#showTextParameter(parameter: ExpressionParameter | StringParameter): void {
		this.inputField.text = parameter.value;
		this.inputField.visible = true;
	}

	/**
	 * Presents a stock item's parameter: text or a number, typed into the field the model's text parameters are.
	 *
	 * A number gets a field that takes only a number. One the engine says the user may not change is shown in the same field, read-only, the way a geometry's code is: it is part of what the item is, and seen where every other row has its value.
	 *
	 * A parameter with no value yet — a number nobody has answered, a read-only text the engine left out — shows an empty field, which is what an open question looks like.
	 */
	#showStockParameter(parameter: IStockConfigurationParameter): void {
		if (parameter instanceof StockStringParameter && parameter.answers !== null) {
			this.#showAnswerList(parameter, parameter.answers);
			return;
		}
		this.inputField.type = parameter instanceof StockNumberParameter ? 'numeric' : 'text';
		this.inputField.text = textOf(parameter);
		this.inputField.readOnly = parameter.isReadOnly;
		this.inputField.visible = true;
	}

	/**
	 * Presents a stock parameter answered from a list: its answers in the dropdown, in the order the engine wants, with the one it holds picked.
	 *
	 * Each answer is offered by its code as a user reads it — `Grå`, not `Gr%C3%A5`. An empty list is an empty dropdown, since the parameter is answered from one all the same. One the engine says may not be changed is shown with its answer picked and the dropdown out of reach, the way a read-only field is.
	 */
	#showAnswerList(parameter: StockStringParameter, answers: ReadonlyArray<StockAnswer>): void {
		const items = answers.map((answer) => new DropdownItem({ text: answer.label, value: answer }));
		this.#answerItems = items;
		this.answerDropdown.data = items;
		this.answerDropdown.enabled = !parameter.isReadOnly;
		this.answerDropdown.visible = true;
		this.#showAnswer(parameter);
	}

	/**
	 * Picks the answer the row stands on in the dropdown, or none while it stands on none. Read afresh every time, so putting an answer back and showing a new parameter are the same thing.
	 *
	 * Found by its code among the answers the dropdown was handed, so none found is a parameter that holds none.
	 */
	#showAnswer(parameter: StockStringParameter): void {
		const picked = this.#pickedAnswer(parameter);
		this.answerDropdown.selectedItem = this.#answerItems.find((item) => item.value instanceof StockAnswer && item.value.key === picked) ?? null;
	}

	/** The code of the answer the row stands on: the one it sent while that is out, or else the one the parameter holds — or null for none. */
	#pickedAnswer(parameter: StockStringParameter): string | null {
		return this.#sentAnswer ?? parameter.answer?.key ?? null;
	}
}

/**
 * What a parameter typed into the row's field holds, as the field shows it.
 *
 * A model's text and expression parameters hold text already. A stock item's may hold a number, which is shown as its digits, or nothing at all, which is shown as an empty field — so the field and the parameter can be compared as text, which is how the row tells a change from none.
 */
function textOf(parameter: ExpressionParameter | IStockConfigurationParameter | StringParameter): string {
	return parameter.value === undefined ? '' : String(parameter.value);
}
customElements.define('cf-answer-item-renderer', AnswerItemRenderer);
